import {
  Group,
  CylinderBufferGeometry,
  MeshBasicMaterial,
  Mesh,
  Vector3,
  TorusBufferGeometry,
  Object3D,
} from "three";
import { GameStateManager } from "../managers/GameStateManager";
import { Frame } from "../util/frame";
import { SceneObjectBase } from "./object";

const HALF_PI = 0.5 * Math.PI;
const plateWidth = 17 / 12;
const thickness = 0.1 / 12;
const halfPlateWidth = 0.5 * plateWidth;
const width = plateWidth; // + 2 * extension;
const material = new MeshBasicMaterial({ color: "#000" });
const n = new Vector3(0, 0, -1); // normal of the strike plane
const p0 = new Vector3(0, 0, -plateWidth); //point in the strike plane
const spotRadius = 0.2;

export class StrikeZone extends SceneObjectBase {
  private box: Group;

  private horizontal: CylinderBufferGeometry;
  private vertical: CylinderBufferGeometry;
  private top: Mesh;
  private bottom: Mesh;
  private left: Mesh;
  private right: Mesh;
  private batID: number;
  private spot: Mesh;
  private ring: Mesh;
  private currBall: Vector3;
  private prevBall: Vector3;
  private dir: Vector3;

  constructor(upper: number = 3.5, lower: number = 1) {
    super();

    this.batID = -1;
    this.currBall = new Vector3(0, 0, 0);
    this.prevBall = new Vector3(0, 0, 0);
    this.dir = new Vector3(0, 0, 0);
    let height = upper - lower;
    let yMid = 0.5 * (lower + upper);

    this.horizontal = new CylinderBufferGeometry(
      thickness,
      thickness,
      width,
      8
    );
    this.vertical = new CylinderBufferGeometry(thickness, thickness, height, 8);

    this.top = new Mesh(this.horizontal, material);
    this.bottom = new Mesh(this.horizontal, material);
    this.left = new Mesh(this.vertical, material);
    this.right = new Mesh(this.vertical, material);

    this.top.position.set(0, upper, -plateWidth);
    this.top.rotation.z = HALF_PI;
    this.bottom.position.set(0, lower, -plateWidth);
    this.bottom.rotation.z = HALF_PI;
    this.left.position.set(-halfPlateWidth, yMid, -plateWidth);
    this.right.position.set(halfPlateWidth, yMid, -plateWidth);

    let disk = new CylinderBufferGeometry(
      spotRadius,
      spotRadius,
      3 * thickness,
      16
    );
    let mat = new MeshBasicMaterial({
      color: "#fff",
      opacity: 1.0,
      transparent: true,
    });
    this.spot = new Mesh(disk, mat);
    this.spot.position.set(0, -5, 0);
    this.spot.rotation.x = HALF_PI;

    let tube = new TorusBufferGeometry(spotRadius + 0.1, 3 * thickness, 2, 24);
    this.ring = new Mesh(tube, mat);
    this.ring.position.set(0, -5, 0);

    this.box = new Group();
    this.box.add(this.top);
    this.box.add(this.bottom);
    this.box.add(this.left);
    this.box.add(this.right);
    this.box.add(this.spot);
    this.box.add(this.ring);
  }

  get object(): Object3D {
    return this.box;
  }

  update(state: GameStateManager, frame: Frame) {
    let current = state.getCurrentPlay();
    if (current && current.playEvents.length != 0) {
      let latest = current.playEvents[current.playEvents.length - 1];

      // If the ID of the current batter has changed, adjust the strike box to his size
      if (this.batID !== current.matchup.batter.id && latest.pitchData) {
        console.log("New batter:", current.matchup.batter.fullName);
        let upper = latest.pitchData.strikeZoneTop;
        let lower = latest.pitchData.strikeZoneBottom;
        let height = upper - lower;

        this.vertical = new CylinderBufferGeometry(
          thickness,
          thickness,
          height,
          8
        );
        this.left.geometry = this.vertical;
        this.right.geometry = this.vertical;

        let yMid = 0.5 * (upper + lower);
        this.top.position.set(0, upper, -plateWidth);
        this.bottom.position.set(0, lower, -plateWidth);
        this.left.position.set(-halfPlateWidth, yMid, -plateWidth);
        this.right.position.set(halfPlateWidth, yMid, -plateWidth);

        this.batID = current.matchup.batter.id;
      }
    }

    if (frame.ballPosition) {
      let p = frame.ballPosition;
      this.currBall.set(p.x, p.z, -p.y);
      /**
       * Check if the z-position of the ball in the frame data is past the strike plane.
       * The plane is extended forward by another plateWidth to account for inconsistencies in frame data.
       * The positive z-axis points away from the pitcher, so -plateWidth*2 extends two plateWidths away
       * from the tip of home plate in the direction of the pitcher.
       */
      if (this.currBall.z >= -plateWidth * 2) {
        this.dir.subVectors(this.currBall, this.prevBall);
        // is the direction of travel pointing towards the catcher or away
        if (this.dir.z > 0) {
          // using the direction from prev to curr, find the point on the strike plane it intersects
          let d = p0.clone().sub(this.prevBall).dot(n) / this.dir.dot(n);
          let pt = this.prevBall.addScaledVector(this.dir, d);
          this.spot.position.set(pt.x, pt.y, -plateWidth);
          this.ring.position.set(pt.x, pt.y, -plateWidth);
          // set prevball far behind home plate to ensure the throw back from the catcher is not counted
          // this will only change back when the ball is past the plane going the other direction
          this.prevBall.set(0, 0, 100);

          // Reset opacity and scale for new pitch
          //@ts-ignore
          this.spot.material.opacity = 1.0;
          this.spot.scale.set(1, 1, 1);
          //@ts-ignore
          this.ring.material.opacity = 1.0;
          this.ring.scale.set(1, 1, 1);
        }
      } else {
        this.prevBall.copy(this.currBall);
      }
    }
    //@ts-ignore
    this.spot.material.opacity *= 0.995;
    this.spot.scale.multiplyScalar(0.995);
    //@ts-ignore
    this.ring.material.opacity *= 0.995;
    this.ring.scale.multiplyScalar(0.995);
  }
}
